import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import {
  useEliteReservation,
  useEliteStructure,
} from "@/contexts/EliteTemporaryContextProvider";
import { useGuestAuth } from "@/contexts/GuestAuthContext";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { ArrowLeft, Award, Building2, Hotel, UserPlus } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function GuestAreaRegister() {
  const { config, profile, isLoading, register } = useGuestAuth();
  const { reservation } = useEliteReservation();
  const { structure } = useEliteStructure();
  const navigate = useNavigate();

  useDocumentTitle("Registrazione Guest Area");

  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (profile) {
      navigate("/guest-area", { replace: true });
    }
  }, [profile, navigate]);

  useEffect(() => {
    const resObj = (reservation as any)?.reservation;
    const firstCheckedIn = (reservation as any)?.alreadyCheckedInGuests?.[0];

    const nameCandidate =
      firstCheckedIn?.name || firstCheckedIn?.firstName || resObj?.name || "";
    const surnameCandidate =
      firstCheckedIn?.surname ||
      firstCheckedIn?.lastName ||
      resObj?.surname ||
      "";
    const emailCandidate = firstCheckedIn?.email || resObj?.email || "";
    const phoneCandidate =
      firstCheckedIn?.tel || firstCheckedIn?.phone || resObj?.phone || "";

    if (!name && nameCandidate) setName(nameCandidate);
    if (!surname && surnameCandidate) setSurname(surnameCandidate);
    if (!email && emailCandidate) setEmail(emailCandidate);
    if (!phone && phoneCandidate) setPhone(phoneCandidate);
  }, [reservation]);

  if (isLoading && !config) {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex flex-col items-center justify-center py-16 px-4 text-center gap-3"
      >
        <Spinner className="size-7 text-primary" />
        <p className="text-sm font-medium text-muted-foreground">
          Caricamento Guest Area in corso...
        </p>
      </div>
    );
  }

  if (!config?.isEnabled) {
    return (
      <div className="max-w-lg mx-auto py-12 px-4">
        <Card className="border border-border bg-card shadow-none">
          <CardHeader className="space-y-1">
            <CardTitle className="text-xl font-bold">
              Guest Area non attiva
            </CardTitle>
            <CardDescription className="text-sm leading-relaxed">
              La Guest Area non è attualmente abilitata per questa struttura.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" className="min-h-11">
              <Link to="/">Torna alla Home</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const scopeTitle = config.isOrganizationCentralized
    ? config.organizationName || structure?.name || "Gruppo Hotel"
    : structure?.name || config.structureName || "Hotel";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!name.trim() || !surname.trim() || !email.trim() || !password) {
      setErrorMessage("Compila tutti i campi obbligatori contrassegnati.");
      return;
    }

    if (password.length < 6) {
      setErrorMessage("La password deve contenere almeno 6 caratteri.");
      return;
    }

    setSubmitting(true);
    try {
      const outcome = await register({
        name: name.trim(),
        surname: surname.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        password,
      });
      if (outcome.requiresVerification) {
        toast.success(
          outcome.message ||
            "Ti abbiamo inviato un'email con il codice OTP e il link di conferma.",
        );
        const sid = outcome.structureId || config.structureId;
        const targetEmail = outcome.email || email.trim();
        navigate(
          `/guest-area/verify?structureId=${sid}&email=${encodeURIComponent(
            targetEmail,
          )}`,
          { replace: true },
        );
        return;
      }
      toast.success(
        `Iscrizione completata in ${config.programName}`,
      );
      navigate("/guest-area", { replace: true });
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ||
        "Impossibile completare la registrazione.";
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto py-4 sm:py-8 space-y-6">
      <div>
        <Button
          asChild
          variant="ghost"
          className="min-h-11 px-3 -ml-3 text-muted-foreground hover:text-foreground gap-2"
        >
          <Link to="/">
            <ArrowLeft className="size-4" aria-hidden="true" />
            <span>Torna alla Home</span>
          </Link>
        </Button>
      </div>

      <section aria-labelledby="register-heading" className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-primary">
            {config.programName}
          </span>
          <Badge variant="secondary" className="text-xs font-medium gap-1">
            {config.isOrganizationCentralized ? (
              <>
                <Building2 className="size-3" aria-hidden="true" />
                <span>Gruppo {scopeTitle}</span>
              </>
            ) : (
              <>
                <Hotel className="size-3" aria-hidden="true" />
                <span>{scopeTitle}</span>
              </>
            )}
          </Badge>
          {config.welcomeBonusPoints > 0 && (
            <Badge variant="outline" className="text-xs font-semibold">
              +{config.welcomeBonusPoints} pt bonus benvenuto
            </Badge>
          )}
        </div>

        <h1
          id="register-heading"
          className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground"
        >
          Attiva la tua tessera fedeltà
        </h1>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {config.isOrganizationCentralized
            ? `Crea il tuo account valido in tutte le strutture del gruppo ${scopeTitle}. Accumuli ${config.pointsPerSpendUnit} pt ogni ${config.spendUnitAmount} € di spesa.`
            : `Crea il tuo account dedicato a ${scopeTitle}. Accumuli ${config.pointsPerSpendUnit} pt ogni ${config.spendUnitAmount} € di spesa tra soggiorni e servizi in struttura.`}
        </p>
      </section>

      <Card className="border border-border bg-card shadow-none">
        <CardContent className="p-5 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {errorMessage && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/40 bg-destructive/10 px-3.5 py-2.5 text-xs sm:text-sm font-medium text-destructive"
              >
                {errorMessage}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="guest-reg-name">Nome *</Label>
                <Input
                  id="guest-reg-name"
                  autoComplete="given-name"
                  placeholder="Il tuo nome"
                  className="min-h-11"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="guest-reg-surname">Cognome *</Label>
                <Input
                  id="guest-reg-surname"
                  autoComplete="family-name"
                  placeholder="Il tuo cognome"
                  className="min-h-11"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="guest-reg-email">Email *</Label>
              <Input
                id="guest-reg-email"
                type="email"
                autoComplete="email"
                placeholder="email@esempio.it"
                className="min-h-11"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="guest-reg-phone">Telefono (opzionale)</Label>
              <Input
                id="guest-reg-phone"
                type="tel"
                autoComplete="tel"
                placeholder="+39 ..."
                className="min-h-11"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="guest-reg-password">
                Password * (minimo 6 caratteri)
              </Label>
              <Input
                id="guest-reg-password"
                type="password"
                autoComplete="new-password"
                placeholder="Scegli una password"
                className="min-h-11"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <Button
              type="submit"
              className="w-full min-h-11 font-semibold gap-2 cursor-pointer"
              disabled={submitting}
            >
              <UserPlus className="size-4" aria-hidden="true" />
              <span>
                {submitting
                  ? "Registrazione in corso..."
                  : "Completa registrazione"}
              </span>
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="rounded-xl border border-border bg-muted/20 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          <Award
            className="size-5 text-primary shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="space-y-0.5">
            <p className="text-sm font-semibold text-foreground">
              Hai già attivato il tuo account?
            </p>
            <p className="text-xs text-muted-foreground">
              Accedi con email e password per vedere il tuo saldo punti.
            </p>
          </div>
        </div>
        <Button
          asChild
          variant="outline"
          className="min-h-11 px-4 shrink-0 shadow-none"
        >
          <Link to="/guest-area/login">Accedi</Link>
        </Button>
      </div>
    </div>
  );
}
